import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Button, Grid, IconButton, Collapse, Divider, useMediaQuery,
  TextField, Pagination, InputAdornment, FormControl, InputLabel, Select, MenuItem,
  Card, CardContent // Adicionado Card para melhor visual
} from '@mui/material';
import { Handshake, FileDownload, ExpandMore, Search, FilterList } from '@mui/icons-material';
import { getDatabase, ref, get } from 'firebase/database';
import firebaseApp, { KEY_REF } from './adm/admin/firebaseConfig';
import { useTheme } from '@mui/material/styles';
import { getConfig } from './adm/ConfigContext';

// --- Funções Auxiliares (Mantidas) ---

function removePTags(html) {
  return html.replace(/<p>/g, '').replace(/<\/p>/g, '').replace(/<ol>.*?<\/ol>/gs, '').replace(/<li>.*?<\/li>/gs, '');
}

function truncateText(text, maxLength) {
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
}

const getMesNome = (mesNum) => {
  const meses = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];
  // Meses no JS (e no Firebase, se você usou parseInt) são 1-based (1=Janeiro)
  return meses[mesNum - 1] || mesNum;
};


// --- Componente Principal ---

export default function Licitacoes() {
  const config = getConfig();
  const [licitacoes, setLicitacoes] = useState([]);
  const [filteredLicitacoes, setFilteredLicitacoes] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  // Novos Estados para Filtros Personalizados
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('Todas'); // 'Ativas', 'Inativas', 'Todas'
  const [filterModalidade, setFilterModalidade] = useState('Todas');
  const [filterAno, setFilterAno] = useState('Todos');
  const [filterMes, setFilterMes] = useState('Todos'); // NOVO: Filtro por Mês

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const itemsPerPage = 5;

  // Memória para armazenar as opções de filtros
  const modalidadesOptions = useMemo(() => ['Todas', ...new Set(licitacoes.map(l => l.modalidade).filter(Boolean))], [licitacoes]);
  const anosOptions = useMemo(() => ['Todos', ...new Set(licitacoes.map(l => l.ano).filter(Boolean).sort((a, b) => b - a))], [licitacoes]);

  // NOVO: Opções de Mês (valor é o número do mês, label é o nome)
  const mesesOptions = useMemo(() => {
    return [
      { value: 'Todos', label: 'Todos' },
      { value: 1, label: 'Janeiro' }, { value: 2, label: 'Fevereiro' },
      { value: 3, label: 'Março' }, { value: 4, label: 'Abril' },
      { value: 5, label: 'Maio' }, { value: 6, label: 'Junho' },
      { value: 7, label: 'Julho' }, { value: 8, label: 'Agosto' },
      { value: 9, label: 'Setembro' }, { value: 10, label: 'Outubro' },
      { value: 11, label: 'Novembro' }, { value: 12, label: 'Dezembro' },
    ];
  }, []);


  // --- 1. Fetching e Ordenação Inicial ---
  useEffect(() => {
    const fetchLicitacoes = async () => {
      try {
        const databaseInstance = getDatabase(firebaseApp);
        const licitacoesRef = ref(databaseInstance, `licitacoes`);
        const snapshot = await get(licitacoesRef);
        const data = snapshot.val();

        if (data) {
          const licitacoesArray = Object.keys(data).map((key) => ({
            id: key,
            date: data[key].data,
            dia: data[key].dia,
            mes: data[key].mes,
            ano: data[key].ano,
            titulo: data[key].titulo,
            descricao: data[key].descricao,
            numero: data[key].numero,
            contrato: data[key].contrato || null,
            horario: data[key].horario || '',
            modalidade: data[key].modalidade || 'N/D',
            documentoUrl: data[key].documentoUrl,
            ativo: data[key].ativo,
          }));

          // Função de ordenação
          const sortByDate = (a, b) => {
            // Cria um Date object para comparação, considerando os campos numéricos (ano, mes, dia)
            // Se algum campo numérico faltar, a ordenação pode ser imprecisa. Assumimos que existem.
            if (!a.ano || !b.ano || !a.mes || !b.mes || !a.dia || !b.dia) {
              // Fallback para campos faltantes: tentar usar o campo 'date' string (DD/MM/AAAA)
              const [d1, m1, y1] = a.date ? a.date.split('/').map(Number) : [0, 0, 0];
              const [d2, m2, y2] = b.date ? b.date.split('/').map(Number) : [0, 0, 0];
              const dateA_fallback = new Date(y1, m1 - 1, d1);
              const dateB_fallback = new Date(y2, m2 - 1, d2);
              return dateB_fallback.getTime() - dateA_fallback.getTime();
            }

            const dateA = new Date(a.ano, a.mes - 1, a.dia);
            const dateB = new Date(b.ano, b.mes - 1, b.dia);
            return dateB.getTime() - dateA.getTime(); // Ordem decrescente (mais recente primeiro)
          };

          const licitacoesAtivas = licitacoesArray.filter((licitacao) => licitacao.ativo).sort(sortByDate);
          const licitacoesInativas = licitacoesArray.filter((licitacao) => !licitacao.ativo).sort(sortByDate);

          const licitacoesOrdenadas = [...licitacoesAtivas, ...licitacoesInativas];

          setLicitacoes(licitacoesOrdenadas);
          setFilteredLicitacoes(licitacoesOrdenadas);
        }
      } catch (error) {
        console.error('Error fetching licitacoes:', error);
      }
    };

    fetchLicitacoes();
  }, []);

  // --- 2. Lógica de Filtragem e Ordenação Unificada ---

  const applyFiltersAndSort = (
    licitacoesToFilter,
    term,
    active,
    modalidade,
    ano,
    mes // NOVO: parâmetro mes
  ) => {
    let tempLicitacoes = licitacoesToFilter;

    // Filtro por termo de busca (título, número, descrição, modalidade)
    if (term) {
      tempLicitacoes = tempLicitacoes.filter(item =>
        item.titulo.toLowerCase().includes(term) ||
        item.numero.toLowerCase().includes(term) ||
        item.descricao.toLowerCase().includes(term) ||
        item.modalidade.toLowerCase().includes(term)
      );
    }

    // Filtro por Atividade (Ativas/Inativas/Todas)
    if (active !== 'Todas') {
      const isActive = active === 'Ativas';
      tempLicitacoes = tempLicitacoes.filter(item => item.ativo === isActive);
    }

    // Filtro por Modalidade
    if (modalidade !== 'Todas') {
      tempLicitacoes = tempLicitacoes.filter(item => item.modalidade === modalidade);
    }

    // Filtro por Ano
    if (ano !== 'Todos') {
      // Converte para string para garantir comparação com o valor do Select
      tempLicitacoes = tempLicitacoes.filter(item => String(item.ano) === String(ano));
    }

    // NOVO: Filtro por Mês
    if (mes !== 'Todos') {
      // O item.mes é o número (ex: 8 para Agosto) e o filtro.mes é o valor do Select (também um número)
      tempLicitacoes = tempLicitacoes.filter(item => item.mes === mes);
    }

    return tempLicitacoes;
  };

  // useEffect para aplicar os filtros sempre que um dos estados de filtro mudar
  useEffect(() => {
    const newFilteredList = applyFiltersAndSort(
      licitacoes,
      searchTerm.toLowerCase(),
      filterActive,
      filterModalidade,
      filterAno,
      filterMes // NOVO: Passando o estado do mês
    );

    setFilteredLicitacoes(newFilteredList);
    setCurrentPage(1); // Volta para a primeira página ao aplicar novos filtros
  }, [licitacoes, searchTerm, filterActive, filterModalidade, filterAno, filterMes]); // NOVO: Dependência filterMes

  // --- 3. Handlers de Eventos ---

  const handleExpandClick = (id) => {
    setExpanded((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleActiveChange = (event) => {
    setFilterActive(event.target.value);
  };

  const handleModalidadeChange = (event) => {
    setFilterModalidade(event.target.value);
  };

  const handleAnoChange = (event) => {
    setFilterAno(event.target.value);
  };

  // NOVO: Handler para o filtro de Mês
  const handleMesChange = (event) => {
    // O valor do Select já vem como número (ou a string 'Todos')
    setFilterMes(event.target.value);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // --- 4. Renderização da Lista e Filtros ---

  const renderListItem = (item) => (
    <Card
      key={item.id}
      itemscope
      itemtype="https://turmalina.tcepb.tc.br/documentation/Bidding"
      sx={{
        backgroundColor: item.ativo ? '#fff' : '#f0f0f0', // Cor de fundo mais suave para inativas
        borderRadius: 2,
        boxShadow: 3, // Sombra mais proeminente
        marginBottom: 3,
        borderLeft: `5px solid ${item.ativo ? config?.color : 'red'}`, // Borda lateral colorida
        opacity: item.ativo ? 1 : 0.9,
      }}
    >
      <CardContent sx={{ padding: 2 }}>
        <Grid container spacing={isMobile ? 1 : 2} direction={isMobile ? "column" : "row"} alignItems="center">

          {/* Coluna 1: Título e Modalidade */}
          <Grid item xs={isMobile ? 12 : 6}>
            <Box display="flex" alignItems="flex-start" flexDirection="column">
              <Typography
                variant={isMobile ? "h7" : "h7"}
                fontWeight="bold"
                color={config?.color}
                sx={{ overflowWrap: 'break-word', mb: 0.5 }}
                itemprop="object"
              >
                {truncateText(item.titulo, 80)}
              </Typography>
              <Typography variant="body2" color="textSecondary" itemprop="bidModality">
                {item.modalidade}
              </Typography>
            </Box>
          </Grid>

          {/* Coluna 2: Número e Data */}
          <Grid item xs={isMobile ? 6 : 2}>
            <Typography variant="body2" color="textSecondary" textAlign={isMobile ? 'left' : 'center'} itemprop="bidID">
              Nº: {item.numero}
            </Typography>
            <Typography variant="body2" color="textSecondary" textAlign={isMobile ? 'left' : 'center'} itemprop="publicationDate">
              Data: {item.date}
            </Typography>
          </Grid>

          {/* Coluna 3: Botões */}
          <Grid item xs={isMobile ? 6 : 4}>
            <Box display="flex" justifyContent={isMobile ? 'flex-end' : 'flex-end'} width="100%">
              <Button
                href={item.documentoUrl}
                target="_blank"
                variant="contained"
                size="small"
                startIcon={<FileDownload />}
                sx={{
                  textTransform: 'uppercase',
                  minWidth: '100px',
                  fontSize: '0.875rem',
                  backgroundColor: config?.color,
                  '&:hover': {
                    backgroundColor: config?.colorSecondary,
                    color: config?.color
                  },
                  mr: 1
                }}
                itemprop="notice"
              >
                Edital
              </Button>

              <Button
                variant="outlined"
                size="small"
                onClick={() => handleExpandClick(item.id)}
                endIcon={<ExpandMore sx={{ transform: expanded[item.id] ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />}
                sx={{
                  textTransform: 'uppercase',
                  minWidth: '110px',
                  fontSize: '0.875rem',
                  borderColor: config?.color,
                  color: config?.color,
                  '&:hover': {
                    backgroundColor: `${config?.color}10`, // Leve cor ao passar o mouse
                    borderColor: config?.colorSecondary,
                  },
                }}
              >
                Detalhes
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Conteúdo Expansível (Detalhes) */}
        <Collapse in={expanded[item.id]} timeout="auto" unmountOnExit>
          <Box sx={{ padding: 2, mt: 2, backgroundColor: '#fafafa', borderRadius: 1, borderTop: '1px solid #eee' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                {item.horario && <Typography variant="body2"><strong>Horário:</strong> {item.horario}</Typography>}
              </Grid>
              <Grid item xs={12} sm={8}>
                <Typography variant="body2" sx={{ mt: 0.5 }} itemprop="object">
                  <strong>Descrição:</strong> {removePTags(item.descricao)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                {item.contrato && <Button
                  href={item.contrato}
                  target="_blank"
                  variant="contained"
                  size="small"
                  startIcon={<FileDownload />}
                  sx={{ mt: 1, mr: 1, textTransform: 'uppercase', backgroundColor: config?.color, '&:hover': { backgroundColor: config?.colorSecondary, color: config?.color } }}
                  itemprop="notice"
                >
                  Contrato
                </Button>}

                {item.aviso && <Button
                  href={item.aviso}
                  target="_blank"
                  variant="contained"
                  size="small"
                  startIcon={<FileDownload />}
                  sx={{ mt: 1, textTransform: 'uppercase', backgroundColor: config?.color, '&:hover': { backgroundColor: config?.colorSecondary, color: config?.color } }}
                  itemprop="notice"
                >
                  Aviso
                </Button>}
              </Grid>
            </Grid>
          </Box>
        </Collapse>

        {/* Aviso de Inatividade */}
        {!item.ativo && (
          <Typography variant="caption" sx={{ color: 'red', fontWeight: 'bold', mt: 1, display: 'block', textAlign: 'center' }}>
            LICITAÇÃO INATIVA.
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  // --- 5. Estrutura Principal de Renderização ---

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]); // Scrolla quando a página muda

  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredLicitacoes.slice(startIndex, startIndex + itemsPerPage);

  return (
    <Box sx={{ padding: 1, paddingLeft: { xs: 3, md: 15 }, paddingRight: { xs: 3, md: 15 } }}>
      <Box sx={{ py: 4, maxWidth: '1400px', mx: 'auto' }}>

        {/* Título e Busca Principal */}
        <Grid container justifyContent="space-between" alignItems="center" mb={2} spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography sx={{ fontSize: 32, color: config?.color, fontWeight: 'bold' }}>
              LICITAÇÕES
            </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Pesquisar por Título, Número, Modalidade ou Descrição..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ backgroundColor: config?.color, height: 2, mb: 3 }} />

        {/* Painel de Filtros Avançados */}
        <Box sx={{ mb: 3, p: 2, border: '1px solid #ccc', borderRadius: 2, backgroundColor: '#f9f9f9' }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, color: '#555' }}>
            <FilterList sx={{ verticalAlign: 'middle', mr: 0.5 }} />
            Filtros 
          </Typography>
          <Grid container spacing={2}>

            {/* Filtro por Atividade */}
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={filterActive} label="Status" onChange={handleActiveChange}>
                  <MenuItem value="Todas">Todas</MenuItem>
                  <MenuItem value="Ativas">Ativas</MenuItem>
                  <MenuItem value="Inativas">Inativas</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Filtro por Modalidade */}
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Modalidade</InputLabel>
                <Select value={filterModalidade} label="Modalidade" onChange={handleModalidadeChange}>
                  {modalidadesOptions.map((mod) => (
                    <MenuItem key={mod} value={mod}>{mod}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Filtro por Ano */}
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Ano</InputLabel>
                <Select value={filterAno} label="Ano" onChange={handleAnoChange}>
                  {anosOptions.map((ano) => (
                    <MenuItem key={ano} value={ano}>{ano}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* NOVO: Filtro por Mês */}
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Mês</InputLabel>
                <Select value={filterMes} label="Mês" onChange={handleMesChange}>
                  {mesesOptions.map((mes) => (
                    // O valor é o número (1-12) ou a string 'Todos'
                    <MenuItem key={mes.value} value={mes.value}>{mes.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>

        {/* Renderização da Lista de Itens */}
        {currentItems.length > 0 ? (
          currentItems.map((item) => renderListItem(item))
        ) : (
          <Typography fontSize={19} align="center" sx={{ color: '#888', mt: 4, p: 3, border: '1px dashed #ccc' }}>
            Nenhuma licitação encontrada com os filtros aplicados.
          </Typography>
        )}

        {/* Paginação */}
        <Box display="flex" justifyContent="center" mt={4}>
          <Pagination
            count={Math.ceil(filteredLicitacoes.length / itemsPerPage)}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            sx={{
              '& .MuiPaginationItem-root': {
                color: config?.color, // Cor dos números
              },
              '& .Mui-selected': {
                backgroundColor: `${config?.color} !important`, // Fundo da página selecionada
                color: '#fff !important',
              }
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}